import TenantCard from "./TenantCard";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { toast } from "../../components/AppToast";
import { getSystemCap, getTenantSettings, updateTenantCapabilities } from "../../services/tenant/tenant";
import ReorderDragger from "./ReorderComponent";
import ModuleAccessCard from "./ModuleAccessCard";
import SettingsFeedback from "./SettingsFeedback";

const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  TENANT_ADMIN: "Tenant Admin",
  PARTNER: "Partner Admin",
};

const formatRole = (userType) => ROLE_LABELS[userType] || "Administrator";

const AdminSettingsContent = () => {

  const { userDetail } = useSelector((state) => state.login);

  const [tenantConfig, setTenantConfig] = useState(null);
  const token = userDetail?.token || localStorage.getItem("token");
  const [modules, setModules] = useState([]);
  const [tenantExtraRootMenus, setTenantExtraRootMenus] = useState([]);

  useEffect(() => {
    // fetch tenant settings when admin settings opens (via tenant service)
    const tenantId = userDetail?.tenantId?._id || userDetail?.tenantId || null;
    if (!tenantId) return;

    (async () => {
      try {
        const result = await getTenantSettings(tenantId);
        const resultCap = await getSystemCap();
        const capList = resultCap?.data;
        if (Array.isArray(capList)) {
          setModules(capList);
        }
        const extraRoots = resultCap?.tenantExtraRootMenus;
        if (Array.isArray(extraRoots)) {
          setTenantExtraRootMenus(extraRoots);
        } else {
          setTenantExtraRootMenus([]);
        }
        // service may return { success,message,data } or direct data object
        const data = result?.data ?? result;
        if (data && (data.cn || data.urls || data._id)) {
          setTenantConfig(data);
        } else {
          toast.warn(data?.message || "No tenant settings found Please add settings.");
        }
      } catch (err) {
        console.error("Failed to load tenant settings", err);
        toast.error("Failed to load tenant settings");
      }
    })();
  }, [userDetail, token]);

  // console.log("userDetail", userDetail);

  const [profile, setProfile] = useState({
    userId: userDetail?.username || "",
    name: userDetail ? `${userDetail?.firstName} ${userDetail?.lastName}` : "",
    email: userDetail?.email || "",
    phone: userDetail?.mobile || "",
    countryCode: userDetail?.countryCode || "",
    organization: userDetail?.tenantId?.tenant_name || "",
    organizationID: userDetail?.tenantId?._id || "",
  });

  // Optional: Update profile if userDetail changes
  useEffect(() => {
    if (userDetail) {
      setProfile({
        userId: userDetail.username || "",
        name: `${userDetail.firstName} ${userDetail.lastName}`,
        email: userDetail.email || "",
        phone: userDetail.mobile || "",
        countryCode: userDetail?.countryCode || "",
        organization: userDetail?.tenantId?.tenant_name || "",
        organizationID: userDetail?.tenantId?._id || "",
      });
    }
  }, [userDetail]);


  const [feedback, setFeedback] = useState(null);
  const [activeSection, setActiveSection] = useState(
    userDetail?.userType === "TENANT_ADMIN" ? "connection" : "modules"
  );

  const handleReorder = async (newOrder) => {
    const capability = { capabilities: newOrder };
    const resultUpdatedCap = await updateTenantCapabilities(capability);
    setModules(resultUpdatedCap.data.tenantSystemCapabilities);
    setFeedback({
      title: "Menu order saved",
      message: "The navigation order is updated for this tenant.",
    });
  };

  const handleSaveModules = async (updatedModules) => {
    const capability = { capabilities: updatedModules };
    const resultUpdatedCap = await updateTenantCapabilities(capability);
    setModules(resultUpdatedCap.data.tenantSystemCapabilities);
    setFeedback({
      title: "Modules updated",
      message: "Access for this tenant now matches the modules you enabled.",
    });
  };

  const sections = useMemo(() => {
    const items = [];
    if (userDetail?.userType === "TENANT_ADMIN") {
      items.push({
        id: "connection",
        label: "Connection",
        icon: "bi-plug",
        kicker: "Integration",
        description: "Company credentials and the API endpoints this tenant syncs with.",
      });
    }
    items.push({
      id: "modules",
      label: "Modules",
      icon: "bi-grid",
      kicker: "Access",
      description: "Choose which capabilities this tenant can use across the portal.",
    });
    if (modules.length > 0) {
      items.push({
        id: "order",
        label: "Menu order",
        icon: "bi-list-ol",
        kicker: "Navigation",
        description: "Drag modules into the order people should see in the menu.",
      });
    }
    return items;
  }, [userDetail?.userType, modules.length]);

  useEffect(() => {
    if (!sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0]?.id || "modules");
    }
  }, [sections, activeSection]);

  const current = sections.find((section) => section.id === activeSection) || sections[0];
  const initials = useMemo(() => {
    const source = profile.organization || profile.name || "Admin";
    return source
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "A";
  }, [profile.organization, profile.name]);

  return (
    <div className="user-settings admin-settings">
      <aside className="settings-rail">
        <div className="settings-identity">
          <div className="settings-avatar" aria-hidden="true">{initials}</div>
          <div className="settings-identity__copy">
            <h1>{profile.organization || "Tenant workspace"}</h1>
            <p>{formatRole(userDetail?.userType)}</p>
            {profile.name ? <span className="settings-org">{profile.name}</span> : null}
          </div>
        </div>

        <nav className="settings-nav" aria-label="Admin settings sections">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`settings-nav__item${activeSection === section.id ? " is-active" : ""}`}
              onClick={() => setActiveSection(section.id)}
              aria-current={activeSection === section.id ? "page" : undefined}
            >
              <span className="settings-nav__icon">
                <i className={`bi ${section.icon}`} />
              </span>
              <span>
                <strong>{section.label}</strong>
                <small>{section.kicker}</small>
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <section className="settings-panel" aria-live="polite">
        {current ? (
          <header className="settings-panel__head">
            <p className="settings-kicker">{current.kicker}</p>
            <h2>{current.label}</h2>
            <p className="settings-lead">{current.description}</p>
          </header>
        ) : null}

        <div className="settings-panel__body">
          {activeSection === "connection" && (
            <TenantCard
              initialConfig={tenantConfig}
              tenantId={userDetail?.tenantId?._id}
              onSaved={setFeedback}
            />
          )}
          {activeSection === "modules" && (
            <ModuleAccessCard
              modules={modules}
              tenantExtraRootMenus={tenantExtraRootMenus}
              onSave={handleSaveModules}
              tenantId={userDetail?.tenantId?._id || userDetail?.tenantId || null}
              userType={userDetail?.userType}
            />
          )}
          {activeSection === "order" && modules.length > 0 && (
            <ReorderDragger items={modules} onChange={handleReorder} />
          )}
        </div>
      </section>

      <SettingsFeedback
        open={Boolean(feedback)}
        title={feedback?.title}
        message={feedback?.message}
        onClose={() => setFeedback(null)}
      />
    </div>
  );
};

export default AdminSettingsContent;
